import type { Color } from "../../common/color.js";
import { Control, type ControlOptions } from "../core/Control.js";
import type { Rect } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import { VideoTexture, type VideoTextureOptions } from "../../engine/VideoTexture.js";
import type { Theme } from "../themes/Theme.js";
import { fitToSizeMode, type ImageSizeMode } from "./ImageControl.js";

export interface VideoControlOptions extends ControlOptions, VideoTextureOptions {
  /** A URL, an element you own, a ready-made texture, or nothing. */
  source?: string | HTMLVideoElement | VideoTexture | null;
  /** How the frame is fitted to the control. Defaults to `fit` — letterboxed. */
  sizeMode?: ImageSizeMode;
  /** 0..1. */
  volume?: number;
  playbackRate?: number;
  /** Painted behind the frame, so a letterboxed video has proper bars. */
  backgroundColor?: Color | null;
  showBorder?: boolean;
  /** Multiplies the sampled color; use it to dim or tint. */
  tint?: Color | null;
}

/**
 * Video, drawn as geometry like everything else.
 *
 * The frame arrives as a texture (see `VideoTexture`) and goes through the
 * ordinary `drawTexture` path, which is the whole point: it clips to its
 * parent, sits in the window's z-order, takes controls on top and scrolls with
 * whatever contains it. A real `<video>` element in the page could do none of
 * that — it would float above the canvas, above every window, in its own
 * rectangle, and the desktop underneath would have no say.
 *
 * The playback surface only. `VideoPlayer` wraps it with a transport bar built
 * from ordinary buttons and a slider; keeping them apart means a game or a
 * kiosk that wants video with no chrome — or with its own — is not paying for
 * one it has to hide.
 */
export class VideoControl extends Control {
  #texture: VideoTexture | null = null;
  #url: string | null = null;
  /** An element handed over before there was a GPU to build a texture with. */
  #pending: HTMLVideoElement | null = null;
  #owned = false;
  #animating = false;
  #options: VideoTextureOptions;

  sizeMode: ImageSizeMode;
  backgroundColor: Color | null;
  showBorder: boolean;
  tint: Color | null;

  constructor(options: VideoControlOptions = {}) {
    super({ width: 320, height: 180, ...options });
    this.sizeMode = options.sizeMode ?? "fit";
    this.backgroundColor = options.backgroundColor ?? { r: 0, g: 0, b: 0, a: 1 };
    this.showBorder = options.showBorder ?? false;
    this.tint = options.tint ?? null;
    this.#options = {
      crossOrigin: options.crossOrigin,
      loop: options.loop,
      muted: options.muted,
      autoplay: options.autoplay,
      smooth: options.smooth,
    };
    if (options.source !== undefined && options.source !== null) this.source = options.source;
    if (options.volume !== undefined) this.volume = options.volume;
    if (options.playbackRate !== undefined) this.playbackRate = options.playbackRate;
  }

  // --- source --------------------------------------------------------------

  get source(): string | HTMLVideoElement | VideoTexture | null {
    return this.#url ?? this.#pending ?? this.#texture;
  }
  set source(v: string | HTMLVideoElement | VideoTexture | null) {
    this.#release();
    if (v === null) {
      this.#url = null;
      this.invalidate();
      return;
    }
    // Either kind of source can be set before the control is in a tree, and
    // the GPU only arrives with the desktop. Both remember what was asked for;
    // `onEnvironmentChanged` finishes the job on attach.
    if (typeof v === "string") {
      this.#url = v;
      this.#openUrl(v);
    } else if (v instanceof VideoTexture) {
      this.#url = null;
      this.#adopt(v, false);
    } else {
      this.#url = null;
      this.#pending = v;
      this.#openPending();
    }
    this.invalidate();
  }

  /** The texture being drawn, or null before one exists. */
  get texture(): VideoTexture | null {
    return this.#texture;
  }

  /** The element doing the decoding. Null until a source is attached. */
  get video(): HTMLVideoElement | null {
    return this.#texture?.video ?? null;
  }

  // --- transport -----------------------------------------------------------

  /**
   * Start playing.
   *
   * The promise rejects when the browser refuses — an unmuted video with no
   * user gesture behind it, most often. Handled rather than left to become an
   * unhandled rejection, and reported as `mediaerror`.
   */
  play(): Promise<void> {
    const video = this.video;
    if (!video) return Promise.resolve();
    return video.play().catch((err: unknown) => {
      this.emit("mediaerror", err);
    });
  }

  pause(): void {
    this.video?.pause();
  }

  /** Pause and rewind. */
  stop(): void {
    const video = this.video;
    if (!video) return;
    video.pause();
    video.currentTime = 0;
  }

  togglePlay(): void {
    if (this.paused) void this.play();
    else this.pause();
  }

  get paused(): boolean {
    return this.video?.paused ?? true;
  }

  get ended(): boolean {
    return this.video?.ended ?? false;
  }

  get currentTime(): number {
    return this.video?.currentTime ?? 0;
  }
  set currentTime(v: number) {
    const video = this.video;
    if (!video) return;
    // Seeking past the end of a finite video throws in some browsers.
    const end = Number.isFinite(video.duration) ? video.duration : Infinity;
    video.currentTime = Math.min(Math.max(0, v), end);
  }

  /** Seconds, or 0 while unknown. `Infinity` for a live stream. */
  get duration(): number {
    const d = this.video?.duration ?? 0;
    return Number.isNaN(d) ? 0 : d;
  }

  /** How much is buffered ahead of the playhead, in seconds. */
  get buffered(): number {
    const video = this.video;
    if (!video) return 0;
    const ranges = video.buffered;
    for (let i = 0; i < ranges.length; i++) {
      if (ranges.start(i) <= video.currentTime && video.currentTime <= ranges.end(i)) {
        return ranges.end(i) - video.currentTime;
      }
    }
    return 0;
  }

  get volume(): number {
    return this.video?.volume ?? 1;
  }
  set volume(v: number) {
    const video = this.video;
    if (video) video.volume = Math.min(1, Math.max(0, v));
  }

  get muted(): boolean {
    return this.video?.muted ?? false;
  }
  set muted(v: boolean) {
    const video = this.video;
    if (video) video.muted = v;
  }

  get loop(): boolean {
    return this.video?.loop ?? false;
  }
  set loop(v: boolean) {
    const video = this.video;
    if (video) video.loop = v;
  }

  get playbackRate(): number {
    return this.video?.playbackRate ?? 1;
  }
  set playbackRate(v: number) {
    const video = this.video;
    if (video) video.playbackRate = v;
  }

  /** Natural size of the video, or null before the metadata has loaded. */
  get videoSize(): { width: number; height: number } | null {
    // Read from the element rather than the texture. The texture is a 1x1
    // placeholder until the first `update` copies a frame into it, so asking it
    // would answer 1x1 to anyone who looked before the first paint — and
    // `sizeToVideo` would take that literally.
    const video = this.video;
    if (!video || video.videoWidth <= 0 || video.videoHeight <= 0) return null;
    return { width: video.videoWidth, height: video.videoHeight };
  }

  /** Resize the control to the video's natural size. */
  sizeToVideo(): void {
    const size = this.videoSize;
    if (size) this.setSize(size.width, size.height);
  }

  /** Release the decoder and the texture. */
  dispose(): void {
    this.#release();
  }

  // --- painting ------------------------------------------------------------

  /** Destination rectangle for the current frame and size mode. */
  destRect(): Rect | null {
    const size = this.videoSize;
    return size ? fitToSizeMode(this.sizeMode, size, this.localRect) : null;
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const r = this.localRect;
    if (this.backgroundColor) rc.fillRect(r, this.backgroundColor, theme.metrics.controlCornerRadius);

    const texture = this.#texture;
    // Recorded before the UI pass opens, so the copy is legal here — and doing
    // it at paint time means the newest decoded frame is the one drawn.
    if (texture) texture.update();

    const dest = texture && texture.ready ? this.destRect() : null;
    if (texture && dest) {
      // "normal" and "center" can overflow; "fill" always crops.
      rc.clipped(r, () => rc.drawTexture(texture, dest, undefined, this.tint ?? undefined));
    } else {
      const message = texture?.error ? "cannot play this video" : this.#url ? "loading…" : "";
      if (message) rc.drawTextCentered(message, r, theme.metrics.font, theme.palette.textSubtle);
    }

    if (this.showBorder) {
      rc.strokeRect(r, theme.palette.controlBorder, 1, theme.metrics.controlCornerRadius);
    }
  }

  protected override onEnvironmentChanged(): void {
    if (this.#url && !this.#texture) this.#openUrl(this.#url);
    if (this.#pending && !this.#texture) this.#openPending();
    // Detached: give the frame clock back, or it holds the desktop awake.
    if (!this.desktop) this.#stopAnimating();
    else if (!this.paused) this.#startAnimating();
  }

  // --- internals -----------------------------------------------------------

  #openUrl(url: string): void {
    const gpu = this.desktop?.gpu;
    if (!gpu) return; // retried by onEnvironmentChanged once attached
    this.#adopt(VideoTexture.fromUrl(gpu, url, this.#options), true);
  }

  #openPending(): void {
    const video = this.#pending;
    const gpu = this.desktop?.gpu;
    if (!video || !gpu) return; // retried by onEnvironmentChanged once attached
    this.#pending = null;
    this.#adopt(new VideoTexture(gpu, video, this.#options), true);
  }

  #adopt(texture: VideoTexture, owned: boolean): void {
    this.#texture = texture;
    this.#owned = owned;
    texture.onFrame = () => this.invalidate();
    const video = texture.video;
    for (const name of MEDIA_EVENTS) video.addEventListener(name, this.#forward);
    if (!video.paused) this.#startAnimating();
    this.invalidate();
  }

  #forward = (e: Event): void => {
    switch (e.type) {
      case "play":
      case "playing":
        this.#startAnimating();
        this.emit("play");
        break;
      case "pause":
        this.#stopAnimating();
        this.emit("pause");
        break;
      case "ended":
        this.#stopAnimating();
        this.emit("ended");
        break;
      case "timeupdate":
        this.emit("timeupdate");
        break;
      case "durationchange":
        this.emit("durationchange");
        break;
      case "volumechange":
        this.emit("volumechange");
        break;
      case "loadedmetadata":
        this.emit("loadedmetadata");
        break;
      case "error":
        this.#stopAnimating();
        this.emit("mediaerror", this.video?.error ?? null);
        break;
    }
    this.invalidate();
  };

  /**
   * Keep the desktop rendering while the picture is moving.
   *
   * Only needed where `requestVideoFrameCallback` is missing: with it, each new
   * frame calls `invalidate` itself and the desktop repaints exactly as often
   * as the video advances, rather than at the display's rate.
   */
  #startAnimating(): void {
    if (this.#animating) return;
    const texture = this.#texture;
    if (texture && texture.usesFrameCallback) return;
    const desktop = this.desktop;
    if (!desktop) return;
    this.#animating = true;
    desktop.addAnimator(this);
  }

  #stopAnimating(): void {
    if (!this.#animating) return;
    this.#animating = false;
    this.desktop?.removeAnimator(this);
  }

  #release(): void {
    this.#stopAnimating();
    this.#pending = null;
    const texture = this.#texture;
    if (!texture) return;
    for (const name of MEDIA_EVENTS) texture.video.removeEventListener(name, this.#forward);
    texture.onFrame = null;
    if (this.#owned) texture.destroy();
    this.#texture = null;
    this.#owned = false;
  }
}

/** Element events subscribed to and re-raised as control events. */
const MEDIA_EVENTS: readonly string[] = [
  "play",
  "playing",
  "pause",
  "ended",
  "timeupdate",
  "durationchange",
  "volumechange",
  "loadedmetadata",
  "error",
];
